/**
 * Generic min/max dual range slider (single visual track) + hidden checkboxes
 * for hardcoded numeric list facets (width / height / depth / weight …).
 *
 * Keeps the historical `facet-width-cm-range` custom-element/class name
 * to stay compatible with the deployed Shopify asset. Reads its per-instance
 * configuration from data-attrs:
 *   data-range-min, data-range-max, data-step
 *   data-unit        (e.g. 'cm', 'kg', 'l')
 *   data-param-min   (URL/hidden input name for the lower bound)
 *   data-param-max   (URL/hidden input name for the upper bound)
 *   data-pill-class  (class hook used by the active-facets pill to live-update)
 *
 * Syncs with FacetFiltersForm via bubbling `input` on the parent form.
 */
class FacetWidthCmRange extends HTMLElement {
  connectedCallback() {
    const jsonEl = this.querySelector('.facet-width-cm-range__json');
    if (!jsonEl) return;
    try {
      this.items = JSON.parse(jsonEl.textContent);
    } catch (e) {
      return;
    }

    this.unit = this.dataset.unit || '';
    this.paramMinName = this.dataset.paramMin || '';
    this.paramMaxName = this.dataset.paramMax || '';
    this.pillClass = this.dataset.pillClass || '';

    this.rangeMin = parseFloat(this.dataset.rangeMin);
    this.rangeMax = parseFloat(this.dataset.rangeMax);
    if (!Number.isFinite(this.rangeMin)) this.rangeMin = 0;
    if (!Number.isFinite(this.rangeMax)) this.rangeMax = 1;

    const step = this.dataset.step || '1';

    this.minInput = this.querySelector('.facet-width-cm-range__input--min');
    this.maxInput = this.querySelector('.facet-width-cm-range__input--max');
    this.groupEl = this.querySelector('.facet-width-cm-range__range-group');
    this.readout = this.querySelector('.facet-width-cm-range__readout');
    this.paramMin = this.querySelector('.facet-width-cm-range__param-min');
    this.paramMax = this.querySelector('.facet-width-cm-range__param-max');
    if (!this.minInput || !this.maxInput) return;

    this.minInput.min = String(this.rangeMin);
    this.minInput.max = String(this.rangeMax);
    this.maxInput.min = String(this.rangeMin);
    this.maxInput.max = String(this.rangeMax);
    this.minInput.step = step;
    this.maxInput.step = step;

    const urlParams = new URLSearchParams(window.location.search || '');
    const qpMin = parseFloat(urlParams.get(this.paramMinName));
    const qpMax = parseFloat(urlParams.get(this.paramMaxName));
    const hasQP = Number.isFinite(qpMin) && Number.isFinite(qpMax);

    if (hasQP) {
      const lo = Math.max(this.rangeMin, Math.min(this.rangeMax, Math.min(qpMin, qpMax)));
      const hi = Math.max(this.rangeMin, Math.min(this.rangeMax, Math.max(qpMin, qpMax)));
      this.minInput.value = String(lo);
      this.maxInput.value = String(hi);
      if (this.paramMin) this.paramMin.value = String(lo);
      if (this.paramMax) this.paramMax.value = String(hi);
      const isFull = Math.abs(lo - this.rangeMin) < 1e-9 && Math.abs(hi - this.rangeMax) < 1e-9;
      if (isFull) this.clearCheckboxes();
      else this.syncCheckboxes(lo, hi);
    } else {
      const activeNums = this.items
        .filter((i) => i.active && !i.disabled)
        .flatMap((i) => {
          if (Array.isArray(i?.nums)) return i.nums.filter((n) => Number.isFinite(n));
          if (Number.isFinite(i?.num)) return [i.num]; // backward compat
          return [];
        });
      const hasActive = activeNums.length > 0;

      if (hasActive) {
        const lo = Math.min(...activeNums);
        const hi = Math.max(...activeNums);
        this.minInput.value = String(lo);
        this.maxInput.value = String(hi);
        this.syncCheckboxes(lo, hi);
        if (this.paramMin) this.paramMin.value = String(lo);
        if (this.paramMax) this.paramMax.value = String(hi);
      } else {
        // Full range selected visually, but keep filter cleared until user interacts.
        this.minInput.value = String(this.rangeMin);
        this.maxInput.value = String(this.rangeMax);
        this.clearCheckboxes();
        if (this.paramMin) this.paramMin.value = '';
        if (this.paramMax) this.paramMax.value = '';
      }
    }

    this.updateReadout();
    this.updateProgress();
    this.updateActivePills();

    if (!window.__wingsFacetRangePillListenerBound) {
      window.__wingsFacetRangePillListenerBound = true;
      document.addEventListener('facets:activeFacetsRendered', () => {
        document.querySelectorAll('facet-width-cm-range').forEach((el) => {
          if (typeof el.updateActivePills === 'function') el.updateActivePills();
        });
      });
    }

    this.boundHandler = () => {
      let a = parseFloat(this.minInput.value);
      let b = parseFloat(this.maxInput.value);
      if (!Number.isFinite(a)) a = this.rangeMin;
      if (!Number.isFinite(b)) b = this.rangeMax;
      if (a > b) {
        const t = a;
        a = b;
        b = t;
      }
      this.minInput.value = String(a);
      this.maxInput.value = String(b);
      // If full range, treat as "no filter"
      const isFull = Math.abs(a - this.rangeMin) < 1e-9 && Math.abs(b - this.rangeMax) < 1e-9;
      if (isFull) {
        this.clearCheckboxes();
        if (this.paramMin) this.paramMin.value = '';
        if (this.paramMax) this.paramMax.value = '';
      } else {
        this.syncCheckboxes(a, b);
        if (this.paramMin) this.paramMin.value = String(a);
        if (this.paramMax) this.paramMax.value = String(b);
      }
      this.updateReadout();
      this.updateProgress();
      this.updateActivePills();
      const form = this.closest('form');
      if (form) form.dispatchEvent(new Event('input', { bubbles: true }));
    };

    this.minInput.addEventListener('input', this.boundHandler);
    this.maxInput.addEventListener('input', this.boundHandler);

    // Make the whole track clickable (pick nearest thumb).
    if (this.groupEl) {
      this.groupEl.addEventListener('pointerdown', (event) => {
        // If user grabbed the thumb, let the native range handle it.
        if (event.target === this.minInput || event.target === this.maxInput) return;
        const rect = this.groupEl.getBoundingClientRect();
        const x = Math.max(0, Math.min(rect.width, event.clientX - rect.left));
        const pct = rect.width ? x / rect.width : 0;
        const rawValue = this.rangeMin + pct * (this.rangeMax - this.rangeMin);
        const stepNum = parseFloat(this.dataset.step) || 1;
        const snapped = Math.round(rawValue / stepNum) * stepNum;
        const v = Math.max(this.rangeMin, Math.min(this.rangeMax, snapped));

        const a = parseFloat(this.minInput.value);
        const b = parseFloat(this.maxInput.value);
        const lo = Number.isFinite(a) ? a : this.rangeMin;
        const hi = Number.isFinite(b) ? b : this.rangeMax;

        // Move the closer thumb.
        if (Math.abs(v - lo) <= Math.abs(v - hi)) {
          this.minInput.value = String(v);
        } else {
          this.maxInput.value = String(v);
        }
        this.boundHandler();
        try {
          event.preventDefault();
        } catch (_) {}
      });
    }
  }

  clearCheckboxes() {
    this.items.forEach((_, i) => {
      const cb = this.querySelector(`input[data-facet-width-cm-index="${i}"]`);
      if (!cb || cb.disabled) return;
      cb.checked = false;
    });
  }

  syncCheckboxes(minV, maxV) {
    this.items.forEach((item, i) => {
      const cb = this.querySelector(`input[data-facet-width-cm-index="${i}"]`);
      if (!cb || cb.disabled) return;
      const nums = Array.isArray(item?.nums)
        ? item.nums.filter((n) => Number.isFinite(n))
        : (Number.isFinite(item?.num) ? [item.num] : []);
      if (nums.length === 0) return;
      // For multi-value entries like "33/38", include only if the whole entry fits in range.
      cb.checked = nums.every((n) => n >= minV - 1e-9 && n <= maxV + 1e-9);
    });
  }

  updateProgress() {
    if (!this.groupEl) return;
    const a = parseFloat(this.minInput?.value);
    const b = parseFloat(this.maxInput?.value);
    const lo = Number.isFinite(a) ? a : this.rangeMin;
    const hi = Number.isFinite(b) ? b : this.rangeMax;
    const span = this.rangeMax - this.rangeMin || 1;
    const minPct = ((lo - this.rangeMin) / span) * 100;
    const maxPct = ((hi - this.rangeMin) / span) * 100;
    this.groupEl.style.setProperty('--range-min', `${Math.max(0, Math.min(100, minPct))}%`);
    this.groupEl.style.setProperty('--range-max', `${Math.max(0, Math.min(100, maxPct))}%`);
  }

  updateReadout() {
    if (!this.readout) return;
    const a = parseFloat(this.minInput?.value);
    const b = parseFloat(this.maxInput?.value);
    const lo = Number.isFinite(a) ? a : this.rangeMin;
    const hi = Number.isFinite(b) ? b : this.rangeMax;
    this.readout.textContent = `${lo} – ${hi}`;
  }

  updateActivePills() {
    const a = parseFloat(this.minInput?.value);
    const b = parseFloat(this.maxInput?.value);
    const lo = Number.isFinite(a) ? a : this.rangeMin;
    const hi = Number.isFinite(b) ? b : this.rangeMax;
    const isFull = Math.abs(lo - this.rangeMin) < 1e-9 && Math.abs(hi - this.rangeMax) < 1e-9;
    if (isFull) return;

    document.querySelectorAll(`.${this.pillClass}`).forEach((el) => {
      const label =
        el.getAttribute('data-wings-range-pill-label') ||
        el.getAttribute('data-wings-width-pill-label') ||
        '';
      el.textContent = `${label}: ${lo} – ${hi}`;
    });
  }
}

if (!customElements.get('facet-width-cm-range')) {
  customElements.define('facet-width-cm-range', FacetWidthCmRange);
}
